import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { initI18n } from '../i18n';
import { FeatureFlags, CouponsConfiguration, GetBootResponse, PublisherData } from '../contracts';
import { Theme } from '../types/theme';
import { API_V1 } from '../services/api.service';
import {
  appThemeAdaptar,
  defaultFeatureFlags,
  getBootFromURLAsync,
  getInitialConfig,
} from '../utils/boot-utils';
import { initCoralogixRum } from '../utils/coralogix-rum';
import { getCheckoutToken } from '../utils/get-checkout-token';
import { getHostDomain } from '../utils/get-host-domain';
import { getSessionToken } from '../utils/get-session-token';
import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

export interface AppConfig {
  theme: Theme;
  publisherId: string;
  supportEmail: string;
  featureFlags?: FeatureFlags;
  couponsConfiguration: CouponsConfiguration;
  isDefaultBoot: boolean;
  publisherData: PublisherData;
}

interface AppConfigExtra {
  isBootInitialized: boolean;
  isParsedUrl: boolean;
  updateFeatureFlags: (featureFlags?: FeatureFlags) => void;
}

export const AppConfigContext = createContext<(Partial<AppConfig> & AppConfigExtra) | null>(null);

export const AppConfigProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();
  const [config, setConfig] = useState<Partial<AppConfig>>(getInitialConfig);
  const [isBootInitialized, setIsBootInitialized] = useState(!config.isDefaultBoot);
  const [isParsedUrl, setIsParsedUrl] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const cot = urlParams.get('cot');

  useEffect(() => {
    const sessionToken = getSessionToken();
    if (sessionToken) {
      initI18n(sessionToken);
    }
  }, []);

  const applyBootConfig = useCallback((boot: GetBootResponse) => {
    setConfig((prev) => ({
      ...prev,
      ...boot,
      theme: appThemeAdaptar(boot.checkoutTheme),
      publisherId: boot.checkoutTheme.publisherId,
      isDefaultBoot: false,
    }));
  }, []);

  const fetchFallbackBoot = useCallback(() => {
    const domain = cot ? '' : getHostDomain();
    const checkoutToken = cot || getCheckoutToken();

    API_V1.getBoot(domain, checkoutToken)
      .then((data) => {
        const appTheme = appThemeAdaptar(data?.checkoutTheme);

        setConfig((prev) => ({
          ...prev,
          theme: appTheme,
          publisherId: data.checkoutTheme.publisherId,
          couponsConfiguration: data.couponsConfiguration,
        }));
      })
      .catch((error) => {
        console.error(error);
        setConfig((prev) => ({
          ...prev,
          featureFlags: defaultFeatureFlags,
        }));
      })
      .finally(() => {
        setIsBootInitialized(true);
      });
  }, [cot]);

  const validateAndApplyBoot = useCallback(async () => {
    try {
      const boot = await getBootFromURLAsync();

      if (!boot) {
        // Tampered or invalid boot — surface fatal error and do not proceed
        showBoundary(t('unableToOpenCheckout'));
        return;
      }

      applyBootConfig(boot);
      setIsParsedUrl(true);
      setIsBootInitialized(true);
    } catch (error) {
      console.error('Boot verification failed', error);
      showBoundary(t('unableToOpenCheckout'));
    }
  }, [applyBootConfig]);

  const hasBootInUrl = useCallback(() => {
    return typeof window !== 'undefined' && window.location.hash.includes('boot=');
  }, []);

  useEffect(() => {
    if (hasBootInUrl()) {
      validateAndApplyBoot();
      return;
    }

    if (!isBootInitialized) {
      fetchFallbackBoot();
    }
  }, [fetchFallbackBoot, hasBootInUrl, isBootInitialized, validateAndApplyBoot]);

  useEffect(() => {
    if (isBootInitialized) {
      initCoralogixRum(config as AppConfig);
    }
  }, [isBootInitialized]);

  const updateFeatureFlags = useCallback((featureFlags?: FeatureFlags) => {
    setConfig((prev) => ({
      ...prev,
      featureFlags,
    }));
  }, []);

  return (
    <AppConfigContext.Provider
      value={{
        ...config,
        isBootInitialized,
        isParsedUrl,
        updateFeatureFlags,
      }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};
